import { saveAs } from "file-saver";
import moment from "moment";
import * as XLSX from "xlsx";
import { DeliveryTypeTitleEnum } from "../enums/DeliveryTypeEnum";
import { capitalizeFirstLetter } from "./CapitalizeFirstLetter";
import ShippingStagesEnum from "../enums/ShippingStagesEnum";
import { PricePolicyEnum } from "../enums/PricePolicyEnum";

function conciliationXLSXFormatter(conciliations) {

  const addressInfoHandled = address => {
    let finalAddress = address?.street;

    if (!!address?.number) {
      finalAddress += `, ${address?.number}`;
    }

    if (!!address?.complement) {
      finalAddress += `, ${address?.complement}`;
    }

    if (!!address?.district) {
      finalAddress += `, ${address?.district}`;
    }

    if (!!address?.city && !!address?.state) {
      finalAddress += ` - ${address?.city}/${address?.state}`;
    }

    return finalAddress;
  };

  const transformToCurrency = number => {
    if (Number.isNaN(number)) return "--"

    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL"
    }).format(Number(number))
  }

  return conciliations.map(conciliation => {
    const status = Object.entries(ShippingStagesEnum).find(st => st[1].id === conciliation?.shipping?.idShippingStatus);
    const policy = conciliation?.shipping?.correios?.politicaDePreco;
    const isPricePolicyFixed = policy?.type === PricePolicyEnum.TYPE.FIXED;

    const priceDiff = Number(conciliation?.priceDiff);
    const actualPrice = Number(conciliation?.shipping?.price);
    const actualWeight = Number(conciliation?.shipping?.weight);
    const actualLength = Number(conciliation?.shipping?.length);
    const actualHeight = Number(conciliation?.shipping?.height);
    const actualWidth = Number(conciliation?.shipping?.width);
    const actualCubWeight = (actualLength * actualHeight * actualWidth) / 6000;

    const valueWithDiscountOriginal = conciliation?.shipping?.correios?.valorComDescontoOriginal;
    const valueWithDiscount = conciliation?.shipping?.correios?.valorComDesconto;
    const discountValue = policy ? (valueWithDiscountOriginal - valueWithDiscount) : 0;

    const actualDiscount = policy?.percentage ? discountValue : 0;

    let oldDiscount
    let discountDiff

    if (isPricePolicyFixed) {
      const oldPrice = priceDiff + valueWithDiscountOriginal;
      oldDiscount = oldPrice - valueWithDiscount;
      discountDiff = priceDiff !== 0 ? 1 : 0
    } else {
      const oldPrice = priceDiff + actualPrice;
      const typePolice = policy?.type === PricePolicyEnum.TYPE.INCREASE ? (1 + policy?.percentage / 100) : (1 - policy?.percentage / 100);
      const oldDiscountPrice = Number(Number(oldPrice / typePolice).toFixed(2));

      oldDiscount = policy?.percentage ? (oldDiscountPrice - oldPrice) : 0;
      discountDiff = policy?.percentage && (actualDiscount !== oldDiscount) ? (!!oldDiscount ? 1 : 0) : 0;
    }

    const billedWeightDiff =
      actualCubWeight > 5 && actualWeight < actualCubWeight ? actualCubWeight : 0;

    const billedWeight =
      actualCubWeight > 5 && actualWeight < actualCubWeight
        ? actualCubWeight.toFixed(2)
        : actualWeight.toFixed(2);

    const conciliedOrigin = `${conciliation?.shipping?.origin?.city}/${conciliation?.shipping?.origin?.state}`;
    const originalOrigin = conciliation?.originCityDiff;

    const seguredValue = Number(conciliation?.shipping?.insurance ? conciliation?.shipping?.correios?.valorValorDeclarado : 0);

    return {
      "Conferência Realizada Em": moment(conciliation?.created_at).subtract(3, "hours").format("DD/MM/YYYY - HH:mm"),
      "Envio": conciliation?.shipping?.name,
      "Etiqueta": conciliation?.shipping?.sro,
      "Cliente": capitalizeFirstLetter(
        conciliation?.shipping?.user?.userData[1]?.Value?.trim()
      ),
      "Destinatário": capitalizeFirstLetter(
        conciliation?.shipping?.destination?.name.trim()
      ),
      "Destino": addressInfoHandled({
        street: conciliation?.shipping?.destination?.street,
        number: conciliation?.shipping?.destination?.number,
        complement: conciliation?.shipping?.destination?.complement,
        district: conciliation?.shipping?.destination?.district,
        city: conciliation?.shipping?.destination?.city,
        state: conciliation?.shipping?.destination?.state
      }),
      "Status": status[1].message,
      "Diferença de Preço": Number(isPricePolicyFixed ? 0 : priceDiff !== 0 ? Number(priceDiff) * -1 : 0) || "--",
      "Origem Original": originalOrigin ?? conciliedOrigin,
      "Origem Conciliada": conciliedOrigin,
      "Altura Original": (Number(conciliation?.heightDiff) + Number(actualHeight)).toFixed(2),
      "Altura Conciliada": Number(actualHeight).toFixed(2),
      "Largura Original": (Number(conciliation?.widthDiff) + Number(actualWidth)).toFixed(2),
      "Largura Conciliada": Number(actualWidth).toFixed(2),
      "Comprimento Original": (Number(conciliation?.lengthDiff) + Number(actualLength)).toFixed(2),
      "Comprimento Conciliado": Number(actualLength).toFixed(2),
      "Formato Original": conciliation?.formatDiff || conciliation?.shipping?.format,
      "Formato Conciliado": conciliation?.shipping?.format,
      "Peso Original": ((Number(conciliation?.weightDiff) + Number(actualWeight)).toFixed(2)),
      "Peso Conciliado": Number(actualWeight).toFixed(2),
      "Peso Faturado Original": (Number(billedWeightDiff) || Number(billedWeight)).toFixed(2),
      "Peso Faturado Conciliado": Number(billedWeight).toFixed(2),
      "Tipo Original": DeliveryTypeTitleEnum[conciliation?.typeDiff],
      "Tipo Conciliado": DeliveryTypeTitleEnum[conciliation?.shipping?.correios?.type],
      "Preço Original": Number(isPricePolicyFixed ? actualPrice : Number(priceDiff) + actualPrice) || "--",
      "Preço Conciliado": Number(actualPrice) || "--",
      "Desconto Original": Number(oldDiscount) || "--",
      "Desconto Conciliado": Number(actualDiscount) || "--",
      "Valor Segurado": seguredValue,
      "Prazo de Entrega": conciliation?.shipping?.correios?.prazoEntrega ? `${conciliation?.shipping?.correios?.prazoEntrega} ${conciliation?.shipping?.correios?.prazoEntrega > 1
        ? "dias úteis"
        : "dia útil"
        }` : '--'
    }
  })
}

function xlsxGenerator(data, fileName) {
  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "data");

  const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const blob = new Blob([wbout], { type: "application/octet-stream" });

  saveAs(blob, fileName);

}

export { xlsxGenerator, conciliationXLSXFormatter }
